import { z } from 'zod'
import { EvalCategoryIdSchema, EvalQuestionIdSchema } from '../recording'
import { SmallLocationDataSchema } from '../locations/model'
import { AccountIdSchema, idSchemaV1, isId, LocationIdSchema, TeamIdSchema } from '../ids'
import { TinyUserSchema } from '../entity'
import { AuditPropsSchema } from '../common'
import { ArchivableSchema } from '../archive'

export const EvaluationIdSchema = idSchemaV1('Eval')
export type EvaluationId = z.infer<typeof EvaluationIdSchema>
export const isEvaluationId = isId(EvaluationIdSchema)

export type WithEvaluationId = {
  evaluationId: EvaluationId
}

export const toEvaluationId = ({ evaluationId }: WithEvaluationId): EvaluationId => evaluationId

export const EvaluationStateSchema = z
  .enum([
    'Pending', // The evaluation is newly created and waiting for a recording to be uploaded
    'Recorded', // The evaluation has an uploaded recording and has not been submitted for feedback
    'PendingScore', // The evaluation has an uploaded recording and is waiting to be graded
    'Rejected', // The recording was deemed not suitable for evaluation (bad audio, too quiet, not a pitch, etc.)
    'Complete', // The evaluation has been graded and is waiting to be acknowledged
    'Acknowledged', // The graded evaluation has been acknowledged
  ])
  .meta({
    id: 'evaluationEvalState',
    description: 'The state of the evaluation',
  })
export type EvaluationState = z.infer<typeof EvaluationStateSchema>

export const EvalAnswerQuestionSchema = z.object({
  code: EvalQuestionIdSchema,
  title: z.string(),
  score: z.number(),
  possible: z.number(),
  comment: z.string().optional(),
})
export type EvalAnswerQuestion = z.infer<typeof EvalAnswerQuestionSchema>

export const EvalAnswerCategorySchema = z.object({
  code: EvalCategoryIdSchema,
  title: z.string(),
  score: z.number(),
  possible: z.number(),
  comment: z.string().optional(),
  questions: z.array(EvalAnswerQuestionSchema),
})
export type EvalAnswerCategory = z.infer<typeof EvalAnswerCategorySchema>

const EvalFeedbackSchema = z.object({
  prompt: z.string(),
  feedback: z.string(),
})
export type EvaluationFeedback = z.infer<typeof EvalFeedbackSchema>

export const EvaluationDataSchema = z.object({
  ...ArchivableSchema.shape,
  ...AuditPropsSchema.shape,
  evaluationId: EvaluationIdSchema,
  evalState: EvaluationStateSchema,
  user: TinyUserSchema,
  teamId: TeamIdSchema.optional().meta({
    id: 'evaluationTeamId',
  }),
  possible: z.number().meta({
    id: 'evaluationPossible',
    description: 'The total possible score for the evaluation',
  }),
  categories: z.array(EvalAnswerCategorySchema),
  selfAssessment: z.array(EvalAnswerCategorySchema).optional(),
  eventDate: z.number().optional().meta({
    id: 'evaluationEventDate',
    description:
      'When the behavior occurred (the recording was submitted), in UTC epoch milliseconds. This could be EMPTY_DATE if this is a Pending evaluation.',
  }),
  evaluator: TinyUserSchema.optional().meta({
    id: 'evaluationEvaluator',
    description: 'The user who conducted (or will conduct) this eval',
  }),
  evalDate: z.number().optional().meta({
    id: 'evaluationEvalDate',
    description: 'When the evaluation was graded, in UTC epoch milliseconds',
  }),
  score: z.number().optional().meta({
    id: 'evaluationScore',
  }),
  comment: z.string().optional().meta({
    id: 'evaluationComment',
    description: 'If the evaluation was rejected, this is the reason',
  }),
  downloadUrl: z.string().optional().meta({
    id: 'evaluationDownloadUrl',
  }),
  uploadUrl: z.string().optional().meta({
    id: 'evaluationUploadUrl',
  }),
  locationId: LocationIdSchema.optional().meta({
    description: 'The location ID where the pitch was recorded',
  }),
  location: SmallLocationDataSchema.optional(),
  accountId: AccountIdSchema.optional().meta({
    id: 'evaluationAccountId',
    description: 'The account associated with the evaluation',
  }),
  feedback: EvalFeedbackSchema.array().optional(),
})
export type EvaluationData = z.infer<typeof EvaluationDataSchema>

export const TinyEvaluationSchema = z.object({
  ...EvaluationDataSchema.pick({
    evaluationId: true,
    teamId: true,
    evalState: true,
    eventDate: true,
    evalDate: true,
    user: true,
    evaluator: true,
    score: true,
    possible: true,
    locationId: true,
    createdAt: true,
    createdBy: true,
  }).shape,
})

export type TinyEvaluation = z.infer<typeof TinyEvaluationSchema>
